import React, { useState, useEffect } from 'react';
import {
  Badge,
  IconButton,
  Menu,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  Chip,
} from '@mui/material';
import { Notifications as NotificationsIcon, Close as CloseIcon } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { Notification } from '../types';
import { notificationsService } from '../services/notificationsService';
import { navigateFromNotification } from '../utils/notificationNavigation';
import LoadingAnimation from './LoadingAnimation';
import { getZoomCompensatedSx } from '../utils/zoomCompensation';

interface NotificationsCenterProps {
  unreadCount: number;
  onUnreadCountChange: (count: number) => void;
}

const NotificationsCenter: React.FC<NotificationsCenterProps> = ({
  unreadCount,
  onUnreadCountChange,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Nautilus Console Design System tokens
  const isDark = theme.palette.mode === 'dark';
  const NAUTILUS = { deepOcean: '#0f2134', sand: '#ccba9b', abyssal: '#0a1622' };
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    loadNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const loadNotifications = async (pageNum: number = 1, append: boolean = false) => {
    setLoading(true);
    try {
      console.log('Loading notifications for page', pageNum);
      const response = await notificationsService.getNotifications(pageNum, 10, false);
      console.log('Received notifications:', response.notifications);
      const newNotifications = append ? [...notifications, ...response.notifications] : response.notifications;
      setNotifications(newNotifications);
      setHasMore(pageNum < response.pagination.pages);
      setPage(pageNum);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId: number) => {
    try {
      await notificationsService.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId ? { ...notif, isRead: true } : notif
        )
      );
      onUnreadCountChange(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleDelete = async (notificationId: number) => {
    try {
      await notificationsService.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
      // Update unread count if deleted notification was unread
      const deletedNotif = notifications.find(n => n.id === notificationId);
      if (deletedNotif && !deletedNotif.isRead) {
        onUnreadCountChange(Math.max(0, unreadCount - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleDeleteAll = async () => {
    if (notifications.length === 0) return;

    try {
      await notificationsService.deleteAllNotifications();
      setNotifications([]);
      onUnreadCountChange(0);
      setHasMore(false);
      setPage(1);
    } catch (error) {
      console.error('Failed to delete all notifications:', error);
    }
  };

  const handleLoadMore = () => {
    loadNotifications(page + 1, true);
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.isRead) {
      handleMarkAsRead(notification.id);
    }

    handleClose();

    try {
      await navigateFromNotification(notification, navigate);
    } catch (error) {
      console.error('Failed to navigate from notification:', error);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'time_off_approved':
        return 'success';
      case 'time_off_rejected':
        return 'error';
      case 'project_assigned':
        return 'primary';
      case 'opportunity_next_action_due':
        return 'warning';
      case 'opportunity_next_action_overdue':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        aria-label="Notifications"
        sx={{
          color: theme => theme.palette.mode === 'dark' ? '#0f2134' : 'inherit'
        }}
      >
        <Badge
          badgeContent={unreadCount}
          sx={{
            '& .MuiBadge-badge': {
              bgcolor: NAUTILUS.sand,
              color: NAUTILUS.deepOcean,
              fontWeight: 'bold'
            }
          }}
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            maxHeight: 350,
            width: 380,
            borderRadius: 1,
            bgcolor: surfaceBg,
            border: `1px solid ${panelBorder}`,
            boxShadow: 'none',
          },
        }}
        sx={getZoomCompensatedSx()}
      >
        <Box sx={{ p: 2, borderBottom: `1px solid ${panelBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem', color: isDark ? '#b0bec5' : '#546e7a' }}>
            Notifications
          </Typography>
          <Button
            size="small"
            onClick={handleDeleteAll}
            disabled={notifications.length === 0 || loading}
            sx={{
              minWidth: 'auto',
              px: 1,
              py: 0.25,
              borderRadius: 1,
              border: `1px solid ${panelBorder}`,
              fontSize: '0.65rem',
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
              '&:hover': {
                borderColor: isDark ? alpha(NAUTILUS.sand, 0.4) : alpha(NAUTILUS.deepOcean, 0.3),
                bgcolor: isDark ? alpha(NAUTILUS.sand, 0.08) : alpha(NAUTILUS.deepOcean, 0.06),
              },
            }}
          >
            Delete All
          </Button>
        </Box>

        {notifications.length === 0 && !loading && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No notifications
            </Typography>
          </Box>
        )}

        <List sx={{ py: 0 }}>
          {notifications.map((notification) => (
            <ListItem
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              sx={{
                borderBottom: `1px solid ${panelBorder}`,
                flexDirection: 'column',
                alignItems: 'stretch',
                py: 1.5,
                px: 2,
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                '&:hover': {
                  bgcolor: isDark ? alpha(NAUTILUS.sand, 0.08) : 'action.hover',
                },
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, justifyContent: 'space-between' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      fontWeight: notification.isRead ? 'normal' : 'bold',
                      pr: 1
                    }}
                  >
                    {notification.message}
                  </Typography>
                  <Chip
                    label={notification.type.replace('_', ' ')}
                    size="small"
                    variant="outlined"
                    sx={{
                      flexShrink: 0,
                      borderRadius: 1,
                      height: 20,
                      fontSize: '0.65rem',
                      borderColor: panelBorder,
                      color: isDark ? '#b0bec5' : 'text.secondary',
                      textTransform: 'uppercase'
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(notification.createdAt)}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {!notification.isRead && (
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsRead(notification.id);
                        }}
                        sx={{
                          minWidth: 'auto',
                          px: 1,
                          fontSize: '0.65rem',
                          color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                          textTransform: 'uppercase',
                          fontWeight: 600
                        }}
                      >
                        Mark Read
                      </Button>
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(notification.id);
                      }}
                      sx={{ p: 0.5 }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </ListItem>
          ))}
        </List>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <LoadingAnimation />
          </Box>
        )}

        {hasMore && !loading && (
          <Box sx={{ p: 1, textAlign: 'center' }}>
            <Button onClick={handleLoadMore} size="small">
              Load More
            </Button>
          </Box>
        )}
      </Menu>
    </>
  );
};

export default NotificationsCenter;